import _ from 'lodash';
import numeral from 'numeral';
import React, { useMemo } from "react";
import { TableColumn } from 'react-data-table-component';
import { DeepPartial } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BooleanValue } from "../components";
import styled from "../themes/themed-styled-components";
import { DangerousGoods } from "../types/graph";

const Cell = styled.div.attrs({ 'data-tag': 'allowRowEvents' })`
    position:relative;
    &:before {
        content: " ";
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        z-index: 1;
        user-select: none;
    }
`;

const translationKeys = (name:string) => [
    `terminal.data.dangerousGoods.columns.${name}.name`,
    `terminal.data.columns.${name}.name`,
    `data.columns.${name}.name`
];

//type UseDangerousGoodsDataProps = {};

const DangerousGoodsAttachmentsList = styled.div`
display: flex;
flex-direction: column;
gap: 1em;
`;

export type DangerousGoodsRow = DeepPartial<DangerousGoods>;

export type DangerousGoodsAttachmentsCellProps = {
    dangerousGoods: DangerousGoods
}

const DangerousGoodsAttachmentsCell = ({ dangerousGoods: { safetyDataSheet, unNumber, adrClass, imdgGateAttachments } }: DangerousGoodsAttachmentsCellProps) => {
    const { t } = useTranslation('common');

    console.log({ safetyDataSheet, unNumber, adrClass, imdgGateAttachments });

    const emergencyCardUrl = `/${t('schede-di-emergenza')}/${adrClass}/${unNumber}`;
    const safetyDataSheetUrl = safetyDataSheet?.downloadUrl ?? null;

    const imdgGateAttachmentLink = useMemo(() => {
        if (imdgGateAttachments === undefined || imdgGateAttachments === null || imdgGateAttachments.length === 0) {
            return null;
        }

        const [imdgGateAttachment] = imdgGateAttachments;
        const { description, downloadUrl } = imdgGateAttachment;

        // @ts-ignore
        return <a href={downloadUrl} target="_blank">{`${description} (IMDG Gate)`}</a>
    }, [imdgGateAttachments]);

    console.log(imdgGateAttachmentLink);

    const emergencyCardLink = useMemo(() => {
        if (emergencyCardUrl === null) {
            return null;
        }

        if (imdgGateAttachmentLink === null) {
            return <Link target="_blank" to={emergencyCardUrl}>{t('Scheda di emergenza')}</Link>
        }

        return <>
            {imdgGateAttachmentLink}
            <Link to={emergencyCardUrl}>{t('Scheda di emergenza (Hacpack)')}</Link>
        </>
    }, [emergencyCardUrl, imdgGateAttachmentLink, t]);

    if (safetyDataSheetUrl !== null) {
        return <a target="_blank" rel="noreferrer" href={safetyDataSheetUrl as string}>{t('Scheda di sicurezza')}</a>;
    }

    return <>{emergencyCardLink}</>;
};

const useDangerousGoodsData = () => {

    const { t } = useTranslation('common');



    const columns = useMemo<TableColumn<DangerousGoodsRow>[]>(() => [
        {
            id: 'id',
            sortField: 'id',
            name: t(translationKeys('id')),
            selector: (row) => row.id?.toString()??'',
            sortable: true,
            omit: process.env.NODE_ENV !== 'development'
        },
        {
            id: 'unNumber',
            sortField: 'unNumber',
            name: t(translationKeys('unNumber')),
            selector: (row) => row.unNumber ?? '',
            sortable: true
        },
        {
            id: 'adrClass',
            sortField: 'adrClass',
            name: t(translationKeys('adrClass')),
            selector: (row) => row.adrClass ?? '',
            sortable: true
        },
        {
            id: 'packingGroup',
            sortField: 'packingGroup',
            name: t(translationKeys('packingGroup')),
            selector: (row) => row.packingGroup ?? '',
            sortable: true
        },
        {
            id: 'properShippingName',
            sortField: 'properShippingName',
            name: t(translationKeys('properShippingName')),
            selector: (row) => row.properShippingName ?? '',
            minWidth: '10em',
            sortable: true,
            wrap: true,
            allowOverflow: false
        },
        {
            id: 'adrSecondaryClasses',
            sortField: 'adrSecondaryClasses',
            name: t(translationKeys('adrSecondaryClasses')),
            selector: (row) => _(row?.adrSecondaryClasses ?? []).join(' '),
            format: (row) => _(row?.adrSecondaryClasses ?? []).join(' '),
            wrap: true,
            allowOverflow: false
        },
        {
            id: 'provision274',
            sortField: 'provision274',
            name: t(translationKeys('provision274')),
            selector: (row) => row.provision274 ?? '',
            sortable: true,
            wrap: true,
            allowOverflow: false
        },
        {
            id: 'weight',
            sortField: 'weight',
            name: t(translationKeys('weight')),
            selector: (row) => row.weight??'',
            sortable: true,
            cell: ({ weight }, index, column, id) => {
                return <Cell>{`${numeral(weight).format('0[.]00')}`} kg</Cell>;
            }
        },
        {
            id: 'packaging',
            sortField: 'packaging',
            name: t(translationKeys('packaging')),
            selector: (row) => row.packaging ?? '',
            sortable: true,
            wrap: true,
            allowOverflow: false
        },
        {
            id: 'numberOfPackages',
            sortField: 'numberOfPackages',
            name: t(translationKeys('numberOfPackages')),
            selector: (row) => row.numberOfPackages ?? '',
            sortable: true
        },
        {
            id: 'limitedQuantity',
            sortField: 'limitedQuantity',
            name: t(translationKeys('limitedQuantity')),
            cell: (row) => <Cell><BooleanValue value={row.limitedQuantity ?? null} /></Cell>,
            sortable: true
        },
        {
            id: 'marinePollutant',
            sortField: 'marinePollutant',
            name: t(translationKeys('marinePollutant')),
            cell: (row) => <Cell><BooleanValue value={row.marinePollutant ?? null} /></Cell>,
            sortable: true
        },
        {
            id: 'flashPoint',
            sortField: 'flashPoint',
            selector: (row) => row.flashPoint ?? '',
            name: t(translationKeys('flashPoint'))
        },
        {
            id: 'attachments',
            name: t(translationKeys('attachments')),
            sortable: false,
            cell: (dangerousGoods, index, column, id) => {
                // @ts-ignore
                return <DangerousGoodsAttachmentsList><DangerousGoodsAttachmentsCell dangerousGoods={dangerousGoods} /></DangerousGoodsAttachmentsList>
            }
        }
    ], [t]);

    return { columns };
};

export default useDangerousGoodsData;